import { useEffect, useState, useCallback } from "react";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { useForm, useWatch } from "react-hook-form";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { assetUrl } from "fusion-core";

import LabelInput from "../../../shared/components/labelInput";
import DriverNameFields from "../../../shared/components/driverNameFields";
import Drawer from "../../../shared/components/Drawer";
import InstructionBadge from "../../../shared/components/InstructionBadge";
import NumberedInstructionStep from "../../../shared/components/NumberedInstructionStep";

import { TAXI_ENABLE_DATEPASSED } from "../../../config/config";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import { handleNext } from "../../../shared/utils/navigationUtils";
import QuoteStep from "../enums/QuoteStep";
import Tq1Mapper from "../mappers/tq1Mapper";
import { fetchTitlesService } from "../../../shared/services/simpleListItemsService";
import NewDatePicker from "../../../shared/components/newDatePicker";
import FieldStack from "../../../shared/components/FieldStack";
import FieldDescription from "../../../shared/components/FieldDescription";
import { generateDLN } from "../../../shared/utils/generateDLN";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import { scrollToTop } from "../../../shared/utils/scrollUtils";
import { FiLock, FiMenu } from "react-icons/fi";
import Button from "../../../shared/components/Button";

const Tq1 = () => {
  const STATE = useAppStateStore();
  const quoteCall = STATE.quoteCall;

  const {
    register,
    formState: { errors, isValid },
    watch,
    control,
    setValue,
    trigger,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: Tq1Mapper.get(),
  });

  useEffect(() => {
    reset(Tq1Mapper.get());
  }, [quoteCall, reset]);

  // State
  const [titles, setTitles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const closeHelpDrawer = useCallback(() => setIsOpen(false), []);

  // Watch values
  const watchFields = ["lastName", "datePassedTest"];
  const gender = watch("title.gender");
  const [lastName, datePassedTest] = watch(watchFields);

  const tooltips = assetUrl(
    "../../../../node_modules/react-tooltip/dist/react-tooltip.css",
  );

  const DOB = useWatch({ control, name: "dateOfBirth" });

  useEffect(() => {
    if (DOB && lastName && gender) {
      const newDLN = generateDLN(lastName, DOB, gender);
      setValue("license", newDLN);
    }
  }, [DOB, lastName, gender, setValue]);

  const MAGIC_DATE_PASSED_TEST = "01/01/2008";

  const convertArrayFormat = useCallback(
    (array) =>
      array.map((item) => ({
        value: item.listItemId,
        label: item.listItemDescription,
        gender: item.listItemExtraInfoValue === "M" ? "Male" : "Female",
      })),
    [],
  );

  const sortTitles = useCallback(
    (titles) =>
      titles.sort((a, b) => {
        const aDescription = a.listItemDescription;
        const bDescription = b.listItemDescription;
        if (
          (aDescription === "Mr" || aDescription === "Mrs") &&
          !(bDescription === "Mr" || bDescription === "Mrs")
        )
          return -1;
        if (
          !(aDescription === "Mr" || aDescription === "Mrs") &&
          (bDescription === "Mr" || bDescription === "Mrs")
        )
          return 1;
        if (aDescription.startsWith("M") && !bDescription.startsWith("M"))
          return -1;
        if (!aDescription.startsWith("M") && bDescription.startsWith("M"))
          return 1;
        return 0;
      }),
    [],
  );

  // // Event Handlers

  // Effects
  useEffect(() => {
    let isMounted = true;
    const fetchAndSetTitles = async () => {
      try {
        setIsLoading(true);
        const titles = await fetchTitlesService();
        const sortedTitles = sortTitles([...titles]);
        if (isMounted) setTitles(sortedTitles);
      } catch (error) {
        // Optionally show a user-friendly error message here
        console.error("Error updating titles:", error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    fetchAndSetTitles();
    return () => {
      isMounted = false;
    };
  }, [sortTitles]);

  useEffect(() => {
    if (datePassedTest) trigger("datePassedTest");
  }, [datePassedTest, trigger]);

  useEffect(() => {
    const datePassed = watch("datePassedTest");
    if (datePassed === MAGIC_DATE_PASSED_TEST) {
      setValue("datePassedTest", null);
    }
  }, [watch, setValue]);

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ1_ENTERED);
  }, []);

  const onSubmit = (data) => {
    if (isValid) {
      TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TQ1_NEXT_CLICKED);
    }
    Tq1Mapper.set(data);
    handleNext(QuoteStep.TQ2);
  };

  // Render
  return (
    <>
      <Helmet>
        <link rel="stylesheet" href={tooltips} />
      </Helmet>
      <QuoteFormWrapper>
        <div className="tw-mb-5 tw-flex tw-min-h-16 tw-items-center tw-gap-3 tw-rounded-[14px] tw-bg-dis-sky-tint tw-px-4 tw-py-3">
          <img
            src={assetUrl("../../../assets/branding/dis-mark-on-white.svg")}
            alt=""
            className="tw-h-8 tw-w-[42px] tw-shrink-0 tw-object-contain"
          />
          <span className="tw-flex tw-flex-col tw-gap-0">
            <span className=" tw-text-base tw-font-bold tw-leading-[1.05] tw-text-dis-navy">
              Driver Insurance Services
            </span>
            <span className="tw-text-sm tw-leading-[1.25] tw-text-dis-text-muted">
              Your quote starts here
            </span>
          </span>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FieldStack className="tw-text-sm tooltip-container">
            <LabelInput
              id="title"
              label="Title"
              type="select"
              placeholder="e.g Mrs"
              options={convertArrayFormat(titles)}
              register={register("title", { required: "Required" })}
              errors={errors?.title}
              watch={watch("title")}
              control={control}
              isLoading={isLoading}
              trigger={trigger}
            />

            <DriverNameFields {...{ register, errors, watch, trigger }} />
            <NewDatePicker
              name="dateOfBirth"
              label="Date of birth"
              errors={errors.dateOfBirth}
              setValue={setValue}
              trigger={trigger}
              control={control}
              rules={{
                required: "Date is required",
              }}
              watch={watch}
              maxDate={new Date()}
            />

            <FieldDescription
              label="Driving licence number"
              description={
                <>
                  We require your Driving Licence Number ("DLN") to provide this
                  to the DVLA to confirm your licence status, entitlement and
                  restriction information and endorsement/conviction data.
                </>
              }
            >
              <div className="tw-mt-3 tw-flex tw-items-start tw-font-medium tw-gap-2.5 tw-text-dis-deep-blue">
                <FiLock
                  className="tw-h-4 tw-w-4 tw-shrink-0 tw-mt-0.5"
                  aria-hidden="true"
                />
                <span>
                  This comes from your Uber account and can only be changed on
                  Uber.
                </span>
              </div>
            </FieldDescription>
            <div className="tw-flex tw-items-start tw-gap-2.5 max-[425px]:tw-flex-col max-[425px]:tw-gap-4">
              <LabelInput
                id="license"
                type="text"
                placeholder="xxxxxxxxxxx"
                disabled={true}
                maxLength={11}
                register={register("license", {
                  required: "Required",
                  pattern: {
                    value: /^[a-zA-Z0-9]{11}$/,
                    message: "Invalid format (xxxxxxxxxxx)",
                  },
                })}
                errors={errors?.license}
                trigger={trigger}
                watch={watch("license")}
                className="tw-min-w-0 tw-flex-[1.75]"
                classNamelic="licFieldAuto"
              />
              <LabelInput
                id="licenseUser"
                type="text"
                placeholder="xxxxx"
                maxLength={5}
                register={register("licenseUser", {
                  required: "Required",
                  pattern: {
                    value: /^[a-zA-Z0-9]{5}$/,
                    message: "Invalid format (xxxxx)",
                  },
                })}
                errors={errors?.licenseUser}
                trigger={trigger}
                watch={watch("licenseUser")}
                className="tw-min-w-0 tw-flex-1"
                classNamelic="licFieldAuto"
              />

              <Button
                onClick={() => setIsOpen(true)}
                size="small"
                className="tw-shrink-0"
                type="button"
              >
                Need help?
              </Button>
            </div>

            {TAXI_ENABLE_DATEPASSED && (
              <NewDatePicker
                name="datePassedTest"
                label="Date Passed Test"
                errors={errors.datePassedTest}
                trigger={trigger}
                setValue={setValue}
                control={control}
                rules={{
                  required: "Date is required",
                }}
                watch={watch}
                maxDate={new Date()}
              />
            )}

            <Button
              type="submit"
              variant={isValid ? "main" : "disabled"}
              fullWidth
              aria-disabled={!isValid}
              className="tw-text-center [caret-color:transparent]"
              onClick={scrollToTop}
            >
              Get started
            </Button>
          </FieldStack>
        </form>
        <Drawer
          isOpen={isOpen}
          onClose={closeHelpDrawer}
          title="Your driving licence number"
        >
          <div className="tw-space-y-5">
            <div className="tw-space-y-4 tw-text-dis-base tw-leading-[1.5] tw-text-dis-text-2">
              <p className="tw-m-0 tw-font-bold tw-text-dis-navy">
                You can only add or change your driving licence number on Uber.
              </p>
              <p className="tw-m-0">
                We pull it through from your Uber account, so it must be correct
                there first. It is the 16 characters in section 5 of your
                photocard driving licence.
              </p>
            </div>

            <ol className="tw-m-0 tw-list-none tw-space-y-4 tw-p-0">
              <NumberedInstructionStep number="1">
                Open the <strong className="tw-font-bold tw-text-dis-navy">Uber Driver app</strong>.
                On a computer, sign in at{" "}
                <a
                  href="https://drivers.uber.com"
                  target="_blank"
                  rel="noreferrer"
                  className="tw-font-bold tw-text-dis-navy"
                >
                  drivers.uber.com
                </a>
                .
              </NumberedInstructionStep>
              <NumberedInstructionStep number="2">
                Tap <InstructionBadge icon={FiMenu}>Menu</InstructionBadge> (or
                your profile picture) at the top left.
              </NumberedInstructionStep>
              <NumberedInstructionStep number="3">
                Tap <InstructionBadge>Account</InstructionBadge>, then{" "}
                <InstructionBadge>Documents</InstructionBadge>.
              </NumberedInstructionStep>
              <NumberedInstructionStep number="4">
                Select <InstructionBadge>Driving licence</InstructionBadge> and
                tap <InstructionBadge>Take photo</InstructionBadge>. Upload the
                front, and the back if Uber asks.
              </NumberedInstructionStep>
              <NumberedInstructionStep number="5">
                Wait for Uber to approve it. Uber usually reviews documents
                within 1 to 5 days.
              </NumberedInstructionStep>
              <NumberedInstructionStep number="6">
                Come back and select{" "}
                <strong className="tw-font-bold tw-text-dis-navy">Get a new quote</strong>.
                We will pull your updated licence number through from Uber.
              </NumberedInstructionStep>
            </ol>

            <p className="tw-m-0 tw-rounded-xl tw-bg-dis-sky-tint tw-p-3.5 tw-text-dis-small tw-leading-[1.55] tw-text-dis-text-2">
              Your licence number on Uber must match your DVLA licence. Use a
              clear, colour photo of the original card showing all 4 corners.
              Uber may also ask for a DVLA check code.
            </p>

            <a
              href="https://help.uber.com/en-GB/driving-and-delivering/article/7---documents---faq?nodeId=f0da3098-a9a0-46c5-bfcb-7b1e8e9c266a"
              target="_blank"
              rel="noreferrer"
              className="tw-inline-block tw-font-bold tw-text-dis-deep-blue tw-underline tw-underline-offset-2 hover:tw-text-dis-navy"
            >
              Full guide: update your licence on Uber
            </a>
          </div>
        </Drawer>
      </QuoteFormWrapper>
    </>
  );
};

export default Tq1;
